/**
 * VolumeTrendsPanel — Recharts multi-series line chart for module_volume_trends.
 *
 * Phase 16 / Task 16.6 (DEC-163). Anonymized series (A/B/C/D/E/Other) returned
 * server-side; component just renders the letter labels with the fixed color
 * palette from the API's series[]. Date-range toggle updates the query param.
 */
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BiDateRange } from '@lodex-pro/shared/validation/disposal-bi-panels';
import { useDisposalBiVolumeTrends } from '../../hooks/useDisposalBiPanels';
import { PanelChrome } from './PanelChrome';
import { DateRangeToggle } from './DateRangeToggle';
import { StatCard } from './StatCard';
import { formatNumber } from '@lodex-pro/shared/number-format';

interface Props {
  readonly onModuleInactive?: () => void;
}

export function VolumeTrendsPanel({ onModuleInactive }: Props): React.JSX.Element | null {
  const [range, setRange] = React.useState<BiDateRange>('30D');
  const { data, isLoading, isError, error } = useDisposalBiVolumeTrends(range);

  // The API throws a generic `Error` with `API 409: ...` for module-inactive.
  React.useEffect(() => {
    if (isError && error !== null && error.message.includes('409')) {
      onModuleInactive?.();
    }
  }, [isError, error, onModuleInactive]);

  if (isError && error !== null && error.message.includes('409')) return null;

  const points = data?.points ?? [];
  const series = data?.series ?? [];

  // Aggregate stats.
  const totalBbl = points.reduce((sum, p) => {
    let dayTotal = 0;
    for (const s of series) {
      const v = p[s.key];
      if (typeof v === 'number') dayTotal += v;
    }
    return sum + dayTotal;
  }, 0);
  const peakDay = points.reduce((peak, p) => {
    let dayTotal = 0;
    for (const s of series) {
      const v = p[s.key];
      if (typeof v === 'number') dayTotal += v;
    }
    return dayTotal > peak ? dayTotal : peak;
  }, 0);
  const avgDaily = points.length > 0 ? totalBbl / points.length : 0;

  return (
    <PanelChrome
      title="Volume Trends"
      description="Daily inbound BBL by delivering hauler (top 5 anonymized)."
      actions={<DateRangeToggle value={range} onRangeChange={setRange} />}
    >
      <div className="mb-4 grid grid-cols-3 gap-3">
        <StatCard label="Total BBL" value={formatNumber(totalBbl, { decimals: 0 })} />
        <StatCard label="Peak Day" value={formatNumber(peakDay, { decimals: 0 })} />
        <StatCard label="Avg Daily" value={formatNumber(avgDaily, { decimals: 0 })} />
      </div>
      <div className="h-72 w-full" data-testid="volume-trends-chart">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-[var(--color-text-tertiary)]">
            Loading…
          </div>
        ) : points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-[var(--color-text-tertiary)]">
            No deliveries in this range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" />
              <XAxis dataKey="date" stroke="var(--color-text-tertiary)" fontSize={11} />
              <YAxis stroke="var(--color-text-tertiary)" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-secondary)',
                  color: 'var(--color-text-primary)',
                }}
                labelStyle={{ color: 'var(--color-text-primary)' }}
              />
              <Legend wrapperStyle={{ color: 'var(--color-text-secondary)', paddingTop: 8 }} />
              {series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </PanelChrome>
  );
}
